import { isScientificNotation } from './object-utils';

/**
 * Decimal point settings for different column IDs
 */
export const decimalPointSettings: Record<string, number> = {
  gbp: 4,
  usd: 4,
  buyRate: 3,
  months: 0,
  fx_rate: 4,
  importedRecords: 0,
  orphans: 0,
  totalBanks: 0,
  iban: 0,
  accountNumber: 0,
  tradedFx: 4,
  settledFx: 4,
  closedFx: 4,
  trade_tradedFx: 4,
};

/**
 * Truncate number while keeping the numeric type (no formatting)
 */
export function truncateNumberKeepType(value: number, decimals = 2): number | string {
  if (!value) return '-';
  const factor = Math.pow(10, decimals);
  return Math.trunc(value * factor) / factor;
}

/**
 * Truncate number and format with locale
 */
export function truncateNumber(value: number, decimals = 2, showZero = false): string {
  if ((!value && !(showZero && value === 0)) || isNaN(value)) return '-';

  const [intPart, decPart = ''] = value.toString().split('.');

  const truncatedDecimals = decPart.slice(0, decimals).padEnd(decimals, '0');

  const result = `${intPart}.${truncatedDecimals}`;
  return Number(result).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Simple number formatter with configurable decimals
 */
export function formatSimpleNumber(number: any, decimal = 2, emptyValue = '-'): string {
  const originalValue = +number;

  if (isNaN(originalValue)) return '';
  
  if (+originalValue !== 0 && originalValue != null) {
    return originalValue.toLocaleString('en-US', {
      minimumFractionDigits: decimal,
      maximumFractionDigits: decimal,
    });
  }
  
  return emptyValue;
}

/**
 * Advanced number formatter with K/M/B abbreviations
 */
export function formatAdvancedNumber(options: {
  number: any;
  decimal?: number;
  sign?: string;
  isShowZero?: boolean;
}): string {
  const { number, decimal = 2, sign, isShowZero = false } = options;
  
  const originalValue = truncateNumberKeepType(number);
  if (originalValue === '-') return isShowZero ? '0' : '-';
  
  if (+number && number !== null) {
    const absValue = Math.abs(Number(originalValue));
    let changedValue: string;

    if (absValue >= 1.0e9) {
      changedValue = (absValue / 1.0e9).toFixed(decimal) + (sign || ' b');
    } else if (absValue >= 1.0e6) {
      changedValue = (absValue / 1.0e6).toFixed(decimal) + (sign || ' m');
    } else if (absValue >= 1.0e3) {
      changedValue = (absValue / 1.0e3).toFixed(decimal) + (sign || ' k');
    } else {
      changedValue = absValue.toFixed(decimal) + (sign || '');
    }

    changedValue = +originalValue < 0 ? `-${changedValue}` : changedValue;
    return changedValue;
  }
  
  return isShowZero ? '0' : '-';
}

/**
 * Number value formatter for AG Grid
 * 
 * @param params - AG Grid params object
 * @param options - Formatting options
 * @param options.useDecimalSettings - Use column-specific decimal settings (default: false)
 * @param options.checkScientificNotation - Check and skip scientific notation (default: false)
 * @param options.useNegativeParentheses - Format negatives as parentheses (default: false)
 */
export function formatNumberValue(
  params: any,
  options: {
    useDecimalSettings?: boolean;
    checkScientificNotation?: boolean;
    useNegativeParentheses?: boolean;
    decimalPoint?: number;
  } = {}
): string {
  const { useDecimalSettings = false, checkScientificNotation = false, useNegativeParentheses = false, decimalPoint } = options;
  
  const originalValue = params.value ? (isNaN(params.value) ? '' : Number(params.value)) : params.value;
  let formattedValue = '-';

  // Handle zero/null
  if (originalValue == 0 || originalValue == null) {
    return '-';
  }
  // Determine decimal places
  let decimal = decimalPoint || 2;
  if (useDecimalSettings && !decimalPoint) {
    decimal =
      decimalPointSettings[params.colDef?.colId] ??
      (decimalPointSettings[params.colDef?.colId] === 0 ? decimalPointSettings[params.colDef?.colId] : 2);
  }

  // Handle pinned rows
  if (params?.node?.rowPinned) {
    formattedValue =
      originalValue?.toLocaleString('en-US', {
        minimumFractionDigits: decimal,
        maximumFractionDigits: decimal,
      }) || '';
    return formattedValue;
  }

  // Check scientific notation if enabled
  if (checkScientificNotation && isScientificNotation(originalValue)) {
    return '-';
  }

  // Format negative with parentheses if enabled
  if (useNegativeParentheses && +originalValue < 0) {
    const absValue = +originalValue.toString().replace(/^-/, '');
    const changed = absValue.toLocaleString('en-US', {
      minimumFractionDigits: decimal,
      maximumFractionDigits: decimal,
    });
    formattedValue = `(${changed})`;
  } else {
    formattedValue = originalValue.toLocaleString('en-US', {
      minimumFractionDigits: decimal,
      maximumFractionDigits: decimal,
    });
  }

  return formattedValue;
}
